import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@finn-auto/design-system/atoms/select';
import { useAuth } from '../../../../contexts/AuthContext';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { JOIN_CONFIG_QUERY } from '../../services/talentJoinApi';
import { buildJoinLink, recruiterVia } from './joinLink';
import { Card, EmptyState, Status } from './ui';

/** The design-system Select allows no empty value, so "no cluster" has a name of its own. */
const ANY_CLUSTER = 'any';

/**
 * How Leads join, per design v0.6 (decision 20 and the Leads row of §4). Sourced is not stored:
 * a sourced person gets one approach and is kept only once they sign up themselves.
 */
const JOIN_STEPS = [
  'A recruiter approaches a sourced person once, for example by InMail, with the sign-up link below. Nothing is stored about them until they sign up themselves.',
  'They can also sign up from the careers page or an event.',
  'They confirm by email (double opt-in). Consent lasts 24 months, like for Vetted Talent.',
  'If a Lead enters a Lever hiring process, their data here is anonymized: Lever holds it from then on. If they are later archived as Vetted Talent, they get a fresh invitation.',
];

/**
 * The one working piece on this page: a recruiter builds the sign-up link for an InMail.
 * The cluster list and the live/preview switch come from the sign-up page's own config, so this
 * card cannot offer a cluster the page does not know or call the page live while it is a preview.
 */
export function LeadsTab() {
  const { user } = useAuth();
  const via = recruiterVia(user?.email);
  const config = useQuery(JOIN_CONFIG_QUERY);
  const [cluster, setCluster] = useState('');
  const [language, setLanguage] = useState<'auto' | 'de' | 'en'>('auto');
  const [copied, setCopied] = useState(false);
  const link = buildJoinLink({ cluster, via, language });
  // Until legal signs off, only @finn.com addresses can sign up, so the link is for testing only.
  const preview = config.data?.live !== true;

  async function copy() {
    if (!via) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      void logAnalyticsEvent('talent_community_link_copied', { has_cluster: Boolean(cluster), language, preview });
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="space-y-5">
      <Card title="How Leads join">
        <ol className="list-decimal space-y-2 pl-5 text-sm text-zinc-700">
          {JOIN_STEPS.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ol>
      </Card>

      <Card
        title="Sign-up link for an InMail"
        aside={
          config.data ? (
            config.data.live ? (
              <Status tone="live">Open to candidates</Status>
            ) : (
              <Status tone="preview">Preview: @finn.com only</Status>
            )
          ) : null
        }
      >
        <p className="mb-4 text-sm text-zinc-600">
          Pick the cluster, copy the link, paste it into your InMail. Every sign-up through it arrives tagged with the
          cluster and with you as the recruiter{via ? ` (${via})` : ''}.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="text-sm text-zinc-700">
            <span className="mb-1 block">Cluster</span>
            <Select value={cluster || ANY_CLUSTER} onValueChange={(v: string) => setCluster(v === ANY_CLUSTER ? '' : v)}>
              <SelectTrigger aria-label="Cluster">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY_CLUSTER}>Let the candidate choose</SelectItem>
                {(config.data?.clusters || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.en}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-zinc-700">
            <span className="mb-1 block">Language of the page</span>
            <Select value={language} onValueChange={(v: string) => setLanguage(v as 'auto' | 'de' | 'en')}>
              <SelectTrigger aria-label="Language of the page">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Candidate&apos;s browser decides</SelectItem>
                <SelectItem value="de">German</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <code className="flex-1 break-all rounded bg-zinc-50 px-3 py-2 text-xs text-zinc-700">{link}</code>
          <Button variant={preview ? 'secondary' : 'primary'} size="sm" onClick={copy} disabled={!via}>
            {copied ? 'Copied' : preview ? 'Copy link for testing' : 'Copy link'}
          </Button>
        </div>
        {preview && (
          <p role="note" className="mt-3 rounded border border-solid border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Do not send this link to candidates yet. The sign-up page is a preview: only @finn.com addresses can sign up
            until legal has approved the consent text.
          </p>
        )}
        {!via && (
          <p role="alert" className="mt-3 text-sm text-red-700">
            Your FINN email address could not be read, so the link would not say who sent it. Reload the page and try
            again.
          </p>
        )}
        <p className="mt-3 text-xs text-zinc-500">
          The cluster list is a placeholder until the final one is confirmed.
          {config.isError && ' The sign-up page could not be reached, so the cluster list is empty.'}
        </p>
      </Card>

      <Card title="Sign-ups">
        <EmptyState title="Not shown here yet">
          People who signed up and confirmed will be listed here, by cluster and recruiter. That is the next build.
        </EmptyState>
      </Card>
    </div>
  );
}
